import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { createRoot } from "react-dom/client";
import { createElement } from "react";
import { toast } from "sonner";
import PrintableBill from "./PrintableBill";

// Render the real PrintableBill (same layout as when printing) off-screen and
// capture it to a canvas so the PDF matches the printed receipt exactly.
async function renderBillCanvas(bill, settings) {
  const host = document.createElement("div");
  host.style.cssText = "position:fixed;left:-10000px;top:0;width:380px;background:#ffffff;z-index:-1;";
  document.body.appendChild(host);
  const root = createRoot(host);

  await new Promise((res) => {
    root.render(createElement(PrintableBill, { bill, settings }));
    setTimeout(res, 300);
  });

  const el = host.querySelector(".printable-bill");
  // Neutralise the on-screen fixed/full-viewport styling for a tight capture.
  Object.assign(el.style, {
    position: "static", inset: "auto", maxWidth: "none", width: "360px",
    margin: "0", boxShadow: "none", padding: "16px", height: "auto", overflow: "visible",
  });

  // Wait for the logo image to load so it appears in the capture.
  const img = el.querySelector("img");
  if (img && !img.complete) {
    await new Promise((r) => { img.onload = r; img.onerror = r; setTimeout(r, 1500); });
  }

  const canvas = await html2canvas(el, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
  root.unmount();
  host.remove();
  return canvas;
}

// Build a PDF invoice styled like the printed receipt (80mm width). Returns a Blob.
export async function buildInvoicePdf(bill, settings = {}) {
  const canvas = await renderBillCanvas(bill, settings);
  const imgW = 80; // mm — matches the 80mm thermal receipt
  const imgH = (canvas.height * imgW) / canvas.width;
  const pdf = new jsPDF({ unit: "mm", format: [imgW, imgH] });
  pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, imgW, imgH);
  return pdf.output("blob");
}

// Share the invoice with the PDF ATTACHED. On mobile the native share sheet
// opens with the PDF + message so you can send it into the customer's WhatsApp
// chat (WhatsApp can't both open a specific chat AND auto-attach a file, so the
// share sheet is the only way to include the PDF). On desktop (no file share),
// it downloads the PDF and opens the customer's chat directly with the message.
export async function shareInvoiceWhatsApp(bill, settings = {}) {
  const shop = settings.shop_name || "Amraiya";
  const name = (bill.customer_name || "").trim() || "there";
  const raw = (bill.customer_phone || "").replace(/\D/g, "");
  const phone = raw ? (raw.length > 10 ? raw : "91" + raw.slice(-10)) : "";
  const msg = `Hey, ${name}\nInvoice no. ${bill.bill_no}\n\nThank you for shopping with us\n${shop}`;

  const blob = await buildInvoicePdf(bill, settings);
  const file = new File([blob], `Invoice-${bill.bill_no}.pdf`, { type: "application/pdf" });

  // WhatsApp drops the caption when a document is attached, so copy the message
  // to the clipboard — the user can paste it into the chat with one tap.
  let copied = false;
  try { await navigator.clipboard.writeText(msg); copied = true; } catch { /* ignore */ }

  // Preferred: native share sheet with the PDF attached (mobile).
  try {
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: `Invoice ${bill.bill_no}`, text: msg });
      toast.success(copied ? "Invoice shared — message copied, just paste it in the chat" : "Invoice shared");
      return "shared";
    }
  } catch (e) {
    if (e && e.name === "AbortError") return "cancelled";
  }

  // Fallback (desktop): save the PDF and open the customer's chat with the message.
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `Invoice-${bill.bill_no}.pdf`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
  window.open(phone ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  toast.success(copied ? "Invoice downloaded and message copied — paste it in the chat that just opened" : "Invoice PDF downloaded — attach it in the chat that just opened");
  return "fallback";
}
